import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useTeam } from './TeamContext';
import { getLeaderboard } from '../shared/storage/supabaseSync';

/**
 * Team leaderboard — ranked table of members, scoped to current team.
 */
export default function LeaderboardPage() {
  const { user, isLocalMode } = useAuth();
  const { currentTeam, hasTeams } = useTeam();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('latest_exam_score');

  useEffect(() => {
    if (isLocalMode) {
      setLoading(false);
      return;
    }
    if (currentTeam) {
      loadLeaderboard();
    } else {
      setMembers([]);
      setLoading(false);
    }
  }, [isLocalMode, currentTeam?.id]);

  const loadLeaderboard = async () => {
    setLoading(true);
    const data = await getLeaderboard(currentTeam?.id);
    setMembers(data);
    setLoading(false);
  };

  if (isLocalMode) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <h2 className="text-xl font-bold text-white mb-4">Team Leaderboard</h2>
        <p className="text-gray-400">Enable Supabase to see team rankings.</p>
      </div>
    );
  }

  if (!hasTeams) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <h2 className="text-xl font-bold text-white mb-4">Team Leaderboard</h2>
        <p className="text-gray-400 mb-4">Join or create a team to see the leaderboard.</p>
        <Link to="/teams" className="text-blue-400 hover:text-blue-300 text-sm">
          Manage Teams
        </Link>
      </div>
    );
  }

  const sorted = [...members].sort((a, b) => {
    const aVal = a[sortBy] ?? -Infinity;
    const bVal = b[sortBy] ?? -Infinity;
    return bVal - aVal;
  });

  const columns = [
    { key: 'latest_exam_score', label: 'Exam Best', format: (v) => v != null ? `${Math.round(v)}%` : '—' },
    { key: 'total_cards_studied', label: 'Cards', format: (v) => v || 0 },
    { key: 'exams_completed', label: 'Exams', format: (v) => v || 0 },
    { key: 'avg_drill_accuracy', label: 'Drill Acc', format: (v) => v != null ? `${Math.round(v * 100)}%` : '—' },
    { key: 'total_study_days', label: 'Days Active', format: (v) => v || 0 },
  ];

  return (
    <div className="px-4 py-6 max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-white mb-1">
          {currentTeam?.name} Leaderboard
        </h1>
        <p className="text-gray-400 text-sm">{members.length} team members</p>
      </div>

      {loading ? (
        <div className="text-center text-gray-500 py-12">Loading leaderboard...</div>
      ) : members.length === 0 ? (
        <div className="text-center text-gray-500 py-12">No team members yet.</div>
      ) : (
        <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="px-4 py-3 text-left text-gray-400 font-medium">#</th>
                <th className="px-4 py-3 text-left text-gray-400 font-medium">Name</th>
                {columns.map(col => (
                  <th
                    key={col.key}
                    onClick={() => setSortBy(col.key)}
                    className={`px-4 py-3 text-right font-medium cursor-pointer hover:text-white transition-colors ${
                      sortBy === col.key ? 'text-blue-400' : 'text-gray-400'
                    }`}
                  >
                    {col.label} {sortBy === col.key ? '▼' : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((member, i) => {
                const isMe = member.user_id === user?.id;
                return (
                  <tr
                    key={member.user_id}
                    className={`border-b border-gray-700/50 ${isMe ? 'bg-blue-900/20' : ''}`}
                  >
                    <td className="px-4 py-3 text-gray-500">
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-medium ${isMe ? 'text-blue-400' : 'text-white'}`}>
                        {member.display_name}
                      </span>
                      {isMe && <span className="text-xs text-blue-500 ml-2">(you)</span>}
                      {member.role === 'admin' && (
                        <span className="text-xs text-purple-400 ml-2">admin</span>
                      )}
                    </td>
                    {columns.map(col => (
                      <td key={col.key} className="px-4 py-3 text-right text-gray-300">
                        {col.format(member[col.key])}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-center mt-4">
        <button onClick={loadLeaderboard} className="text-sm text-gray-500 hover:text-gray-300">
          Refresh
        </button>
      </div>
    </div>
  );
}
